import { Observable, Subscription } from 'rxjs';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../core/store';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Franchise } from '../../../core/store/franchises/franchise.model';
import { Project } from './../../../core/store/projects/project.model';
import { Severity } from '../../../core/store/types';
import { Division, ProjectCurrency, Warehouse } from '../../../core/store/models';
import * as assetImportsActions from '../../../core/store/asset-imports/asset-imports.actions';
import * as projectCurrencyActions from '../../../core/store/project-currencies/project-currency.actions';
import { projectCurrencySelectors } from '../../../core/store/selectors';
import { MatDialog } from '@angular/material/dialog';
import { InvalidSubDepartmentModalComponent } from '../invalid-sub-department-modal/invalid-sub-department-modal.component';

@Component({
    selector: 'ah-assets-upload',
    templateUrl: './assets-upload.component.html',
    styleUrls: ['./assets-upload.component.scss'],
    standalone: false
})
export class AssetsUploadComponent implements OnInit, OnDestroy, AfterViewInit {
    projectList$: Observable<Project[]>;
    warehouseList$: Observable<Warehouse[]>;
    projectCurrencies: ProjectCurrency[] = [];
    selectedCurrencyId: number;
    projects: Project[];
    selectedProjectId: number;
    selectedWarehouseId: number;
    disableUpload = true;
    currentFranchise: Franchise;
    currentDivision: Division;
    project: Project;
    errorMsg: any[] = [];
    isDivision = false;
    private subs: Subscription;
    studioId: number;
    isShowDragContent: boolean = false;

    constructor(private route: ActivatedRoute, private router: Router, private store: Store<fromRoot.State>, private dialog: MatDialog) { }

    ngOnInit(): void {
        const isFranchise = this.route.snapshot.pathFromRoot.filter(pfr => pfr.routeConfig && pfr.routeConfig.path === 'franchise/:franchiseId');

        this.subs = new Subscription();

        if (isFranchise.length > 0) {
            this.isDivision = false;
            this.projectList$ = this.store.select(fromRoot.getSelectedFranchiseProjects);
            this.subs = this.store.select(fromRoot.getSelectedFranchise).subscribe((franchise) => {
                this.currentFranchise = franchise;
                this.studioId = franchise.studio_id;
            });
            this.store.dispatch(new projectCurrencyActions.ListAction(this.currentFranchise.id));

            this.subs.add(this.store.select(projectCurrencySelectors.getForAssetImportCurrenciesForSelectedProject).subscribe(projectCurrencies => {
                this.projectCurrencies = projectCurrencies
                const base = projectCurrencies.find(pc => pc.base);
                if (base) {
                    this.selectedCurrencyId = base.id;
                }
                this.projectCurrencies = projectCurrencies.sort((a, b) => a.accounting_code < b.accounting_code ? -1 : a.accounting_code > b.accounting_code ? 1 : 0);
            }));
        }
        else {
            this.isDivision = true;
            this.warehouseList$ = this.store.select(fromRoot.getDivisionWarehouses);

            this.subs = this.store.select(fromRoot.getSelectedDivision).subscribe((division) => {
                this.currentDivision = division;
                this.studioId = division.studio_id;
            });
        }
    }

    ngAfterViewInit(): void {
        const uploadButtons = Array.from(document.getElementsByClassName('p-ripple p-element p-button p-component p-disabled') as HTMLCollectionOf<HTMLElement>);
        for (const [i, element] of uploadButtons.entries()) {
            if (i > 0) {
                element.setAttribute('style', 'background-color: #ff4409 !important; color: white !important;opacity: 1;')
            }
        }
    }

    selectProject(id) {
        this.selectedProjectId = id;
        this.disableUpload = false;
        this.store.dispatch(new assetImportsActions.SelectProjectAction(id))

    }

    selectWarehouse(id) {
        this.selectedWarehouseId = id;
        this.disableUpload = false;
    }

    onBeforeSend() {
        this.disableUpload = true;
    }

    onBeforeUpload(event: { formData: FormData }) {
        if (this.currentFranchise) {
            event.formData.append('franchise_id', this.currentFranchise.id.toString());
            event.formData.append('project_id', this.selectedProjectId.toString());
            event.formData.append('origin_currency', this.selectedCurrencyId.toString());
        }
        else {
            event.formData.append('division_id', this.currentDivision.id.toString());
            event.formData.append('warehouse_id', this.selectedWarehouseId.toString());
        }
    }

    onFileSelect($event) {
        this.isShowDragContent = $event.currentFiles.length > 0;
    }

    onRemove() {
        this.isShowDragContent = false;
    }

    onUpload(event: { originalEvent: HttpResponse<any>; files: File[] }) {
        const request = event.originalEvent;
        if (request && request.status === 200) {
            const response = request.body;
            this.store.dispatch(new assetImportsActions.UploadDoneAction({
                importId: response.assetImports.id,
                studioId: this.studioId,
                divisionId: response.assetImports.division_id,
                franchiseId: response.assetImports.franchise_id,
                warehouseId: response.assetImports.warehouse_id,
                projectId: response.assetImports.project_id,
                valueExamples: response.assetImports.value_examples,
                originCurrency: response.assetImports.origin_currency,
                assetsCount: response.assetsCount
            }));

            this.router.navigate(['../confirm-assets-import'], { relativeTo: this.route });
        }
        this.disableUpload = false;
    }

    onError({ error }: { error: HttpErrorResponse; files: File[] }) {
        let message = '';

        if (error.error.message.includes('Unsupported asset template format')) {
            message = 'The file you uploaded cannot be processed. Please compare the file to our template to confirm the column headings have not been altered or removed.';
            this.errorMsg.push({ severity: Severity.Error, summary: 'Invalid Format', detail: message });
            setTimeout(() => { this.errorMsg = []; }, 9000);
        } else if (error.error.message.indexOf('Department(s) do not exist:') == 0) {
            let departments = error.error.message.replace('Department(s) do not exist:', '').trim();
            const departmentCodes = JSON.parse(departments);

            this.dialog.open(InvalidSubDepartmentModalComponent, {
                data: {
                    unmatchedDepartments: departmentCodes.departments.join(', ')
                }
            });
        } else if (error.error.message.indexOf('Sub Department(s) do not exist:') == 0) {
            let subDepartments = error.error.message.replace('Sub Department(s) do not exist:', '').trim();
            const subDepartmentCodes = JSON.parse(subDepartments);

            this.dialog.open(InvalidSubDepartmentModalComponent, {
                data: {
                    unmatchedSubDepartments: subDepartmentCodes.subDepartments
                }
            });
        } else if (error.error.message.indexOf('Missing Department Codes') == 0) {
            message = 'The file you uploaded cannot be processed. Please check that each row that contains an Asset Name also contains a corresponding Department Code';
            this.errorMsg.push({ severity: Severity.Error, summary: 'Missing Department Codes', detail: message });
            setTimeout(() => { this.errorMsg = []; }, 9000);
        } else if (error.error.message.includes('Read-Only Access')) {
            message = '';
            this.errorMsg.push({ severity: Severity.Error, summary: error.error.message, detail: message });
            setTimeout(() => { this.errorMsg = []; }, 9000);
        } else {
            message = 'Upload Error Please try again. If this issue persists, please contact our Support Team at support@synconset.com';
            this.errorMsg.push({ severity: Severity.Error, summary: 'Upload Error', detail: message });
            setTimeout(() => { this.errorMsg = []; }, 9000);
        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}
